import { Optional, Component, OnInit, AfterViewInit, OnChanges, OnDestroy } from '@angular/core';
import { Input, forwardRef } from '@angular/core';
import { Output, EventEmitter } from '@angular/core';
import { ChangeDetectionStrategy, ChangeDetectorRef } from '@angular/core';
import { AbstractControl, FormGroupDirective } from '@angular/forms';
import { FormGroup, FormControl, Validators } from '@angular/forms';
import { ControlContainer, ControlValueAccessor }  from "@angular/forms";
import { NG_VALUE_ACCESSOR } from "@angular/forms";

import { ModalController } from '@ionic/angular';

import { Subscription, Subject } from 'rxjs';
import { HttpClient } from '@angular/common/http';

import { Address } from '@unpispas/upp-base';
import { GeocodePosition } from '@unpispas/upp-base';
import { geocodeService } from '@unpispas/upp-base';
import { languageSupport, languageService } from '@unpispas/upp-base';
import { toastService } from '@unpispas/upp-base';
import { viewService } from '@unpispas/upp-base';

import { AppConstants } from '@unpispas/upp-defs';

/**
 * Interface representing error information for form validation.
 */
interface ErrorInfo {
    [key: string]: string;
}

/**
 * Interface representing map-related information.
 */
interface MapInfo {
    initialized: boolean,
    validated: boolean,
    
    zoom: number,
    formatted: string | null,
    accuracy: number,
    position: {
        lat: number, 
        lng: number
    },
    markers: {
        position: {
            lat: number, 
            lng: number
        }
    }[]
}

/**
 * Interface representing an address with detailed location data.
 */
export interface MapAddress {
    formatted: string;
    address: string;
    number: string;
    door: string | null;
    locality: string;
    timezone: string;

    street: string;
    zipcode: string;
    town: string;
    province: string;
    country: string;

    accuracy: number;
    latitude: number;
    longitude: number;
}

/** Primitive strings are not `instanceof String`; use this for `string | MapAddress` unions. */
function isAddressString(v: string | MapAddress | null | undefined): v is string {
    return typeof v === 'string';
}

/*************************************/
/* UPP-ADDDRESS MODAL COMPONENT      */
/*************************************/

/**
 * Component for displaying an address modal.
 * It allows users to select, validate, and manipulate addresses.
 */
@Component({
    selector: 'upp-modal-address',
    changeDetection: ChangeDetectionStrategy.OnPush,
    templateUrl:'./md/upp-address.html',
    styleUrls: [ 
        './md/upp-address.scss'
    ]
})
export class UppMdAddressComponent extends languageSupport implements OnInit, OnDestroy {
    /** Modal title. */
    @Input() title = '';
    /** Input value, which can be a string or a MapAddress object. */
    @Input() value: string | MapAddress | null = null;

    /** Object containing map information. */
    public _mapinfo: MapInfo = {
        initialized: false,
        validated: false,
        
        zoom: 17,
        formatted: null,
        accuracy: 0,
        position: {
            lat: 0, 
            lng: 0
        },
        markers: [{
            position: {
                lat: 0, 
                lng: 0
            }
        }]
    };
    
    /** Address object storing the resolved address data. */
    private address: MapAddress | null = null;

    /** Observable for Google Maps API load status. */
    private _gmapsApiLoaded = new Subject<boolean> ();
    public gmapsApiLoaded = this._gmapsApiLoaded.asObservable();

    /** Retrieves the Google Maps API key. */
    get GoogleMapId(){
        return AppConstants.GoogleMapKey;
    }

    /**
     * Constructor for dependency injection.
     * @param lang Service for language translation.
     * @param change Change detection service.
     * @param geocode Geocode service for resolving addresses.
     * @param toast Service for displaying toast notifications.
     * @param modalCtrl Controller for managing modals.
     * @param http HTTP client service for API requests.
     */    
    constructor(private lang: languageService, private change: ChangeDetectorRef, private geocode: geocodeService, private toast: toastService, private modalCtrl: ModalController, private http: HttpClient) {
        super(lang, null);

        // https://github.com/angular/components/tree/master/src/google-maps
        if (!geocode.GMAP_API_LOADED) {
            this.geocode.LoadGoogleMaps(this._gmapsApiLoaded);
        }
        else {
            console.warn("[GOOGLE MAPS] API already loaded");
            setTimeout(() => {
                this._gmapsApiLoaded.next(true);
            }, 0);
        }
    }
    
    /** Lifecycle hook: Initializes component state. */
    ngOnInit() {
        if (this.value){
            // form data not provided (only address string)
            if (isAddressString(this.value)){
                const _value = this.value;

                this.geocode.ResolveAddress(_value).then(
                data => {
                    if (data){
                        this._onGeocodeResponse(data, true);
                        this._markAsValid(true);    
                    }
                });
            }

            // form data has been provided 
            else {  
                this.address = this.value as MapAddress;

                this._mapinfo.formatted = this.address.formatted;
                this._mapinfo.position = this._mapinfo.markers[0].position = {
                    lat: Number(this.address.latitude),
                    lng: Number(this.address.longitude)
                }

                this._markAsValid(true);                
            }
        }

        else {
            const _value : MapAddress = {
                formatted: '',
                address: '',
                number: '',
                door: '',
                locality: '',

                street: '',
                zipcode: '',
                town: '',
                province: '',
                country: '',
                
                timezone: 'GMT',

                accuracy: 0,
                latitude: 0,
                longitude: 0
            }

            this.address = _value;
        }
    }
    
    /** Lifecycle hook: Cleans up resources on destroy. */
    ngOnDestroy(){
        super.OnDestroy();
    }

    private _addressForm: FormGroup | null = null;

    /**
     * Returns the FormGroup for the address form.
     * Initializes the form if it hasn't been created yet.
     */    
    get AddressForm() : FormGroup {
        if (this._addressForm == null){
            const _address = this.address ? this.address.address : null;
            const _number = this.address ? this.address.number : null;
            const _door = this.address ? this.address.door : null;
            const _locality = this.address ? this.address.locality : null;

            this._addressForm = new FormGroup({
                address: new FormControl(_address, [
                    Validators.required,
                ]),
                number: new FormControl(_number, [
                    // no validators required 
                ]),
                door: new FormControl(_door, [
                    // no validators required 
                ]),
                locality: new FormControl(_locality, [
                    Validators.required,
                ])
            })
        }
        return this._addressForm;
    }

    /**
     * Handles geocoding response and updates address data.
     * @param address The resolved address object.
     * @param _keepcoords Whether to retain previous coordinates.
     */
    private _onGeocodeResponse(address: Address, _keepcoords = false): void {
        if (!this.address){
            return;
        }

        this.address.formatted = address.formatted;
        this.address.address = address.street.route;
        this.address.number = address.number;
        this.address.locality = address.postal_code + ' ' + address.street.locality + ',  ' + address.area.level2 + ', ' + address.area.level1;

        this.address.street = address.street.route;
        this.address.zipcode = address.postal_code;
        this.address.town = address.street.locality;
        this.address.province = address.area.level2;
        this.address.country = address.area.level3;

        if (!_keepcoords){
            this.address.latitude = address.location.lat;
            this.address.longitude = address.location.lng;
        }

        this.AddressForm.patchValue({
            address: this.address.address,
            number: this.address.number,
            door: this.address.door,
            locality: this.address.locality
        })

        this._mapinfo.formatted = this.address.formatted;
        this._mapinfo.position = this._mapinfo.markers[0].position = {
            lat: Number(this.address.latitude),
            lng: Number(this.address.longitude)
        }

        this.geocode.RequestTimezne(address['location']['lat'], address['location']['lng']).then(
        data => {
            if (this.address){
                this.address.timezone = data;  
            }
        });
    }
    
    /**
     * Marks the form as valid or invalid.
     * @param init Whether the initialization phase is complete.
     */    
    private _markAsValid(init: boolean){
        setTimeout(() => {
            this._mapinfo.validated = true;
            if (init){
                this._mapinfo.initialized = true;
                this.AddressForm.markAsPristine();
            }
            
            this.change.markForCheck();
        }, 100);
    }

    /**
     * Handles form changes and marks the form as invalid until validation.
     */    
    onFormChanged() {
        this._mapinfo.validated = false;
        this.change.markForCheck();
    }
    
    /** Closes the modal and returns the selected address. */
    async AcceptModal(): Promise <void> {
        let result = null

        if (this.address){
            this.address.door = this.AddressForm.get('door')?.value || null;
            if (this._mapinfo.validated) {
                result = this.address; 
            }
        }
        
        (document.activeElement as HTMLElement)?.blur();
        await this.modalCtrl.dismiss(result);
    }    

    /** Dismisses the modal without returning data. */
    async CloseModal(): Promise <void> {
        (document.activeElement as HTMLElement)?.blur();
        await this.modalCtrl.dismiss(null);
    }

    /**
     * Handles map click event to update position.
     * @param event Map click event containing latitude and longitude.
     */
    onMapClick(event: any): void {
        if (!this.address){
            return;
        }

        this.address.accuracy = this._mapinfo.accuracy = 10;
        this._mapinfo.markers[0].position = {
            lat: this.address.latitude = event.latLng.lat(),
            lng: this.address.longitude = event.latLng.lng()
        }
    }

    /**
     * Handles successful geolocation and updates address data.
     * @param position The geolocation position object.
     */    
    private _onLocateSuccess(position: GeocodePosition): void {
        this._mapinfo.accuracy = position.coords.accuracy;

        this.geocode.RequestAddress(position.coords.latitude, position.coords.longitude).then(
        data => {
            this.toast.HideWait();   

            if (data && this.address){
                this._onGeocodeResponse(data);
                this.address.accuracy = this._mapinfo.accuracy;
                this._markAsValid(false);
            }
            else {
                this.toast.ShowAlert('danger', this.lang.tr('@address_zero_results'));
            }
        });
    }
    
    /**
     * Handles geolocation errors and displays an alert.
     * @param err Geolocation error object.
     */    
    private _onLocateError(err: GeolocationPositionError) {
        this.toast.HideWait();        
        this.toast.ShowAlert('danger', this.lang.tr('@geolocation_error', [ err.message, err.code.toString() ]));
    }
        
    /**
     * Attempts to locate the user using geolocation.
     */    
    onLocate() {
        if (this.geocode.CanGeolocate) {
            this.toast.ShowWait();
            this.geocode.GetCurrentPosition(this._onLocateSuccess.bind(this), this._onLocateError.bind(this));
        }
        else { 
            this.toast.ShowAlert('danger', this.tr('@geolocation_not_supported'));
        }
    }
    
    /**
     * Validates the entered address by resolving it via the geocode service.
     */      
    onValidate() {
        const _address = this.AddressForm.value.address + ", " + this.AddressForm.value.number + ", " + this.AddressForm.value.locality;
        this.toast.ShowWait();
        this.geocode.ResolveAddress(_address).then(
        data => {
            this.toast.HideWait();        
        
            if (data && this.address) {
                this._onGeocodeResponse(data);
                this.address.accuracy = 25;
                this._markAsValid(false);
            }
            else {
                this.toast.ShowAlert('danger', this.lang.tr('@address_zero_results'));
            }
        });
    }
}

/*************************************/
/* UPP-ADDDRESS ERROR COMPONENT      */
/*************************************/

/**
 * A component for displaying validation errors for form controls.
 * Automatically integrates with Angular reactive forms using `formControlName`.
 */
@Component({
    selector: 'upp-er-address',
    changeDetection: ChangeDetectionStrategy.OnPush,
    templateUrl:'./er/upp-address.html',
    styleUrls: [ 
        './er/upp-address.scss'
    ]
})
export class UppErAddressComponent implements OnChanges {
    /** The formGroup provided to the related upp-input. */
    @Input() form: FormGroup | null = null;
    /** The name of the form control to track errors for. */
    @Input() name: string | null = null;
    /** A mapping of error keys to their respective error messages. */
    @Input() errornfo: ErrorInfo | null = null
    /** Whether the error messages should be centered. */
    @Input() centered = false;
    /** Forces OnChanges to be called on value change. */
    @Input() value: string | MapAddress | null = null;

    /** The list of active error keys. */
    public errors: string[] = []
    
    /** The form control associated with this component. */
    public control: AbstractControl | null = null;

    /**
     * Constructor for dependency injection.
     */    
    constructor(){
        // nothing to do
    }

    /**
     * Lifecycle hook. Updates the list of errors when inputs change.
     */    
    ngOnChanges() {
        if (this.form && this.name){
            this.control = this.form.get(this.name);
        }

        if (this.control){
            this.errors = [];
            if (this.errornfo && this.control.errors){
                this.errors = Object.keys(this.control.errors).filter(key => Object.prototype.hasOwnProperty.call(this.errornfo, key))
            }    
        }
    }
}

/*************************************/
/* UPP-ADDDRESS INPUT COMPONENT      */
/*************************************/

/**
 * Component for address input with an integrated modal picker.
 */
@Component({
    selector: 'upp-address',
    changeDetection: ChangeDetectionStrategy.OnPush,
    templateUrl:'./upp-address.html',
    styleUrls: [ 
        './upp-address.scss'
    ],
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => UppAddressComponent),
            multi: true
        }            
    ]
})
export class UppAddressComponent implements ControlValueAccessor, OnInit, AfterViewInit, OnDestroy {
    /** Form control name for integration with Angular forms. */
    @Input() formControlName = '';
    /** Placeholder text for the input field. */
    @Input() placeholder = '';
    /** Title of the input field. */
    @Input() title = '';
    /** Whether the address picker should open automatically. */
    @Input() auto = false;
    /** A mapping of error keys to their respective error messages. */
    @Input() errornfo: ErrorInfo = {}     

    /** Associated form group (if used within a form). */
    public formGroup: FormGroup | null = null
    /** Form control instance. */
    public formControl: AbstractControl | null = null;    

    /** Internal value storage. */
    private _innerValue: string | MapAddress | null = null;  

    /* eslint-disable @typescript-eslint/no-unused-vars */
    /** Callback to notify Angular Forms when the value changes. */
    private _onChangeCallback = (v: any) => {
        // nothing to do (required by angular)
    }
    /** Callback to notify Angular Forms when the input is touched. */
    private _onTouchCallback = () => {
        // nothing to do (required by angular)
    }
    /* eslint-enable @typescript-eslint/no-unused-vars */

    private _showtitle = false;

    /**
     * Determines whether the title should be displayed.
     * @returns {boolean} True if the title should be displayed; otherwise, false.
     */    
    get ShowTitle(): boolean {
        return this._showtitle;
    }

    /**
     * Gets the displayed title.
     */   
    get viewTitle(): string {
        return (this.ShowTitle ? this.title : this.placeholder) || '';
    }

    /**
     * Gets the displayed value.
     */    
    get viewValue(): string  {
        if (this._innerValue == null) {
            return this.placeholder;
        }
        return isAddressString(this._innerValue)
            ? this._innerValue
            : this._innerValue.formatted;
    }

    /**
     * Constructor for dependency injection.
     * @param controlContainer Optional control container for form integration.
     * @param change Change detection service.
     * @param modalCtrl Controller for managing modals.
     * @param view App view/session state (online flag).
     * @param toast Toast notifications.
     * @param lang Language / i18n service.
     */
    constructor(
        @Optional() private controlContainer: ControlContainer,
        private change: ChangeDetectorRef,
        private modalCtrl: ModalController,
        private view: viewService,
        private toast: toastService,
        private lang: languageService,
    ) {
        // nothing to do
    }
    
    private _form_subscription: Subscription | null = null;

    /** Lifecycle hook: Initializes the component. */
    ngOnInit() {
        this._innerValue = this.value;
        
        if (this.controlContainer && this.controlContainer instanceof FormGroupDirective){
            this.formGroup = this.controlContainer.form;
        }

        this.formControl = (this.formGroup && this.formControlName) ? this.formGroup.get(this.formControlName) : null;
        if (this.formControl){
            this.value = this.formControl.value || null;

            this._form_subscription = this.formControl.valueChanges.subscribe(
            () => {
                this.value = this.formControl?.value || null;
            });
        }

        this.onChange();        
    }

    /** Lifecycle hook: Called after view initialization. */
    ngAfterViewInit() {
        if (this.auto){
            this.showPicker();
        }
    }
    
    /** Lifecycle hook: Cleans up resources on destroy. */    
    ngOnDestroy(){
        if (this._form_subscription){
            this._form_subscription.unsubscribe();
            this._form_subscription = null;
        }
    }

    /** Emits when the input value changes. */
    @Output() Changed = new EventEmitter<MapAddress | null>();  
    
    /** Handles value changes. */   
    onChange() {
        const v = this._innerValue;
        if (v == null) {
            this._showtitle = false;
        } else if (isAddressString(v)) {
            this._showtitle = v.trim() !== '';
            this.Changed.emit(null);
        } else {
            this._showtitle = v.formatted !== '';
            this.Changed.emit(v);
        }

        this.change.markForCheck();
    }

    /**
     * Shows the address picker modal when online.
     * Offline, shows a warning and does not open the picker.
     */
    async showPicker() {
        if (!this.view.IsOnline) {
            await this.toast.ShowAlert('warning', this.lang.tr('@offline_warning'));
            return;
        }

        const modalValue: string | MapAddress | null =
            this._innerValue == null
                ? null
                : isAddressString(this._innerValue)
                  ? this._innerValue
                  : (JSON.parse(JSON.stringify(this._innerValue)) as MapAddress);

        const modal = await this.modalCtrl.create({
            component: UppMdAddressComponent,
            componentProps: {
                title: this.title,
                value: modalValue
            },
            cssClass: 'modal-address'
        });

        modal.onDidDismiss().then((detail) => {
            if ((detail !== null) && (detail.data != null)) {
                this.value = JSON.parse(JSON.stringify(detail.data)) as MapAddress;
            }
            this.onChange();
        });

        return await modal.present();
    }
    
    /********************************************/
    /* get/set accesors                         */
    /********************************************/
    
    @Input()

    /**
     * Gets the current value of the input.
     * This represents the internal state of the input field.
     * 
     * @returns {ReturnedAddress} The current value of the input.
     */       
    get value(): string | MapAddress | null {
        return this._innerValue;
    }

    /**
     * Sets a new value for the input.
     * This updates the internal state, triggers the `onChange` and `onTouch` callbacks, 
     * and emits the `uppChanged` event.
     * 
     * @param {ReturnedAddress} v - The new value to set.
     */      
    set value(v: string | MapAddress | null) {
        if ((this._innerValue == null) || (v == null) || (JSON.stringify(v) != JSON.stringify(this._innerValue))){
            this._innerValue = v;
            this.onChange();
            
            this._onChangeCallback(v);
            this._onTouchCallback();
        }
    }    
    
    /********************************************/
    /* ControlValueAccessor                     */
    /********************************************/
    
    /**
     * Writes a value to the input. Required by `ControlValueAccessor`.
     * @param {string} value - The value to write.
     */    
    writeValue(value: string | MapAddress | null) {
        if (value !== this._innerValue) {
            this.value = value;
            this.onChange();
        }        
    }

    /**
     * Registers a callback function to be called when the input value changes.
     * Required by `ControlValueAccessor`.
     * @param {Function} fn - The callback function.
     */    
    registerOnChange(fn: any) {
        this._onChangeCallback = fn;
    }

    /**
     * Registers a callback function to be called when the input is touched.
     * Required by `ControlValueAccessor`.
     * @param {Function} fn - The callback function.
     */    
    registerOnTouched(fn: any) {
        this._onTouchCallback = fn;
    }  
}
